import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useQuickSearchQuery } from '@/api/product/quickSearch';
import { QuickSearchResult } from '@/api/product/types';
import { Button, ProductCard, ProductCardProps } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { Area, PageType } from '@/generated/data.schemas';
import { useGetLayout } from '@/generated/layout';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useProductPrices } from '@/helpers/useProductPrices';
import { breakpoints } from '@/types/breakpoints';
import styles from './Results.module.scss';

interface ResultsProps {
	results: QuickSearchResult;
}

export const Results: React.FunctionComponent<ResultsProps> = ({ results }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);
	const staticLinks = layout?.data.staticLinks;

	const [loading] = React.useState(false);
	const { data: settings } = useGetSettings(segmentationId);
	const [hoverSuggestion, setHoverSuggestion] = React.useState<string | undefined>(undefined);

	const { data: hoverResults } = useQuickSearchQuery(
		hoverSuggestion || '',
		!!hoverSuggestion,
		settings?.data.searchSettings,
		settings?.data.isSalesPortal ?? true,
	);
	const [isExpanded, setIsExpanded] = React.useState(false);
	const resultsToDisplay = hoverSuggestion && hoverResults ? hoverResults : results;

	const getSearchRoute = (phrase: string): { pathname: string; search: string } => {
		const url = staticLinks?.find((item) => item.pageType === PageType.Search)?.url || '';

		return {
			pathname: url,
			search: `?phrase=${encodeURIComponent(phrase)}`,
		};
	};

	const handleMouseEnter = (getCurrentTarget): void => {
		// Need to get the currentTarget this way,
		// as it wasn't being passed via the debounced handler
		const currentTarget = getCurrentTarget();

		const {
			dataset: { phrase },
		} = currentTarget;

		if (!phrase || phrase === hoverResults?.phrase) {
			return;
		}

		setHoverSuggestion(phrase || '');
	};

	const debouncedMouseEnterHandler = debounce(handleMouseEnter, 300);

	const handleDebouncedMouseEnter = (event): void => {
		const currentTarget = event.currentTarget;

		debouncedMouseEnterHandler(() => currentTarget);
	};

	// cancel to prevent searching when quick hover on suggestion
	const cancelDebouncedMouseEnter = () => {
		debouncedMouseEnterHandler.cancel();
	};

	const expandSuggestions = (): void => {
		setIsExpanded(true);
	};

	const productCards: ProductCardProps[] = useMemo(() => {
		return (resultsToDisplay.products ?? []).map((product) => ({ product }));
	}, [resultsToDisplay.products]);

	const { productsWithPrices, isPricesLoading, isPricesError } = useProductPrices(productCards, false);

	return (
		<div className={styles.wrapper}>
			{results && (
				<div className={styles.column}>
					<div className={styles.titleWrapper}>
						<h3 className={styles.title}>{translations?.data.shared.quicksearch.suggestions}</h3>
					</div>

					<ul className={classNames(styles.list, { [styles.expanded]: isExpanded })}>
						{results.searchSuggestions.map((item) => (
							<li key={item}>
								<Button
									className={classNames(styles.itemLink, {
										[styles.selected]: item === resultsToDisplay.phrase,
									})}
									variant={'ghost'}
									isFullWidth
									to={getSearchRoute(item)}
									state={{ area: Area.StaticPages, pageType: PageType.Search }}
									data-phrase={item}
									onMouseEnter={handleDebouncedMouseEnter}
									onMouseLeave={cancelDebouncedMouseEnter}
								>
									{item}
								</Button>
							</li>
						))}
					</ul>

					{!isExpanded && results.searchSuggestions.length > 4 && (
						<Button className={styles.showMore} variant={'linkButton'} onClick={expandSuggestions}>
							{translations?.data.shared.quicksearch.showAll}
						</Button>
					)}
				</div>
			)}

			{resultsToDisplay.products && (
				<div className={styles.column}>
					<div className={styles.titleWrapper}>
						<h3 className={styles.title}>{translations?.data.shared.quicksearch.products}</h3>

						{resultsToDisplay.totalHits > 4 && (
							<Button
								to={getSearchRoute(resultsToDisplay.phrase || '')}
								state={{ area: Area.StaticPages, pageType: PageType.Search }}
								variant={'linkButton'}
							>
								{translations?.data.shared.quicksearch.showAll}
							</Button>
						)}
					</div>

					<ul className={styles.grid}>
						{loading ? (
							<>
								{Array.from(Array(2)).map((_, i) => (
									<li key={i}>
										<ProductCard isSkeleton />
									</li>
								))}
							</>
						) : (
							<>
								{productsWithPrices.map((product, index) => (
									<li key={product.product?.id ?? index}>
										<ProductCard
											{...product}
											isPricesLoading={isPricesLoading}
											isPricesError={isPricesError}
											responsiveSizesSettings={`(min-width: ${breakpoints.md}px) 185px, 50vw`}
										/>
									</li>
								))}
							</>
						)}
					</ul>
				</div>
			)}
		</div>
	);
};
